import { initializePhoenix } from "@amzn/ads-phoenix-type-script-client";
import { authConfigStore } from "@amzn/aquila-feedback-web-assets";
import type { Stage } from "@amzn/aquila-service-type-script-client";
import { getUsername } from "@amzn/midway-cognito-type-script-provider";

import { getAquilaApiCredentials } from "../api/aquilaApi/aquilaApi";
import { getBlinkApiClientV2 } from "../api/blinkApi/blinkApi";
import { getWebsiteSettingsFromConfig } from "../api/websiteSettings";
import { authStore } from "../store/auth";
import { FeatureFlagsStore, getFlagValues } from "../store/featureFlags";
import { salesforceStore } from "../store/salesforce";
import { UserPreferencesStore } from "../store/userPreferences";

import { isTestSiteOrDev } from "./environment.utils";
import {
    getAdvertiserId,
    getMarketplaceId,
    getSpoofAsUser,
    isValidAdvertiserId,
} from "./permission.utils";
import { loadUserPreferences } from "./userPreferences.utils";

export const startup = async (): Promise<void> => {
    const isValidId = isValidAdvertiserId();
    const settings = getWebsiteSettingsFromConfig();
    authStore.getState().setSettings(settings);
    const asyncCalls = [
        (async () => {
            let username: string = await getUsername();

            const flags = await getFlagValues(username);
            FeatureFlagsStore.getState().setFeatureFlags(flags);

            const preferences = await loadUserPreferences();
            UserPreferencesStore.getState().setPreferences(preferences, false);

            if (flags.supportMode && getSpoofAsUser() && isTestSiteOrDev()) {
                username = getSpoofAsUser()!;
            }
            authStore.getState().setUsername(username);

            // Set up Aquila Auth Config
            const credentials = await getAquilaApiCredentials();
            authConfigStore.getState().setAuthConfig({
                awsCredentials: {
                    ...credentials,
                    accountId: settings.blinkAwsAccountId,
                },
                stage: settings.stage.toUpperCase() as Stage,
                userAlias: username,
            });

            await initializePhoenix({
                serviceName: "InsightsHub",
                traceDomains: [authStore.getState().settings.blinkApiEndpoint],
                userAlias: username,
                salesForceAccount: getAdvertiserId(),
                marketplaceId: getMarketplaceId(),
                appMonitorId: settings.phoenixRumAppId,
                identityPoolId: settings.phoenixCognitoPoolId,
                katalConfig: {
                    siteName: "AdsSalesforce",
                    stage: settings.stage,
                    region: settings.region,
                },
            });
        })(),
        (async () => {
            if (isValidId) {
                const advertiserId = getAdvertiserId();

                try {
                    const { data } = await (
                        await getBlinkApiClientV2()
                    ).getSfContext({
                        advertiserId,
                    });
                    if (data) {
                        salesforceStore.getState().setSfContext(data);
                    }
                } catch (error) {
                    console.error(
                        "Error fetching SfContext with ID:",
                        advertiserId,
                        error,
                    );
                }
            }
        })(),
    ];

    await Promise.all(asyncCalls);
};
